import type { ReactNode } from "react";
import LoadingDots from "ui/components/LoadingDots";
import cx from "ui/cx";
import type { WithTooltipProps } from "ui/popovers/Tooltip";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./Button.module.scss";

export type ButtonSize = "jumbo" | "large" | "medium" | "small" | "xsmall" | "none";
export type ButtonStyle =
  | "primary"
  | "secondary"
  | "tertiary"
  | "quaternary"
  | "link"
  | "remove"
  | "progress"
  | "completed"
  | "green"
  | "white"
  | "black"
  | "noStyle";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, WithTooltipProps {
  btnStyle?: ButtonStyle;
  btnSize?: ButtonSize;
  fullWidth?: boolean;
  isPending?: boolean;
  pendingClassName?: string;
  pendingChildren?: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  dataTestId?: string;
}

export default function Button({
  children,
  btnStyle = "primary",
  btnSize = "jumbo",
  isPending = false,
  pendingClassName,
  pendingChildren,
  fullWidth = false,
  onClick,
  title,
  disabled,
  dataTestId,
  tooltip,
  ...btnProps
}: ButtonProps) {
  const classNames = cx(
    {
      styles,
      wrapper: true,
      [btnStyle]: true,
      [btnSize]: true,
      pending: isPending,
      fullWidth,
    },
    btnProps.className ?? "",
  );

  return wrapWithTooltip(
    <button
      {...btnProps}
      onClick={onClick}
      className={classNames}
      disabled={disabled || isPending}
      title={disabled ? title : undefined}
      data-testid={dataTestId}
    >
      {pendingClassName ? (
        <span className={pendingClassName}>
          {!isPending ? children : pendingChildren}
          {isPending && <LoadingDots />}
        </span>
      ) : (
        <>
          {!isPending ? children : pendingChildren}
          {isPending && <LoadingDots />}
        </>
      )}
    </button>,
    tooltip,
  );
}
